import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle, CircleAlert, Info, X } from "lucide-react";
import type { ToastType } from "../store/uiStore";

const TINT: Record<ToastType, { bg: string; icon: string; Icon: typeof Info }> = {
  success: { bg: "bg-success-bg", icon: "text-success", Icon: CheckCircle },
  error: { bg: "bg-danger-bg", icon: "text-danger", Icon: CircleAlert },
  warning: { bg: "bg-warning-bg", icon: "text-warning", Icon: AlertTriangle },
  info: { bg: "bg-info-bg", icon: "text-info", Icon: Info },
};

interface AlertProps {
  type: ToastType;
  message: ReactNode;
  /** Info alerts render without an icon unless asked. */
  showIcon?: boolean;
  dismissable?: boolean;
  onDismiss?: () => void;
  /** Right-aligned actions such as Retry. */
  actions?: ReactNode;
  className?: string;
}

export function Alert({ type, message, showIcon = type !== "info", dismissable = false, onDismiss, actions, className }: AlertProps) {
  const tint = TINT[type];
  const Icon = tint.Icon;

  return (
    <div role={type === "error" ? "alert" : "status"} className={`section-reveal rounded-lg px-4 py-3 text-sm text-gray-800 ${tint.bg} ${className ?? ""}`}>
      <div className="flex items-center gap-3">
        {showIcon && <Icon size={20} className={`shrink-0 ${tint.icon}`} aria-hidden="true" />}
        <div className="flex-1">{message}</div>
        {actions}
        {dismissable && (
          <button type="button" onClick={onDismiss} aria-label="Dismiss" className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded-lg">
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
